package net.lateinit.bug_or_feature.site.util

import kotlinx.browser.document
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import net.lateinit.bug_or_feature.shared.model.Prompt
import org.w3c.dom.HTMLLinkElement
import org.w3c.dom.HTMLMetaElement
import org.w3c.dom.HTMLScriptElement
import kotlin.js.unsafeCast

private const val SITE_NAME = "개발자 지옥 밸런스게임"
private const val SITE_DESCRIPTION = "버그냐 기능이냐, 개발자들의 현실 밸런스 게임. 실전형 선택 질문을 탐색하고 직접 질문을 추가해보세요."
private const val SITE_LOCALE = "ko_KR"
private const val SITE_LANGUAGE = "ko-KR"
private const val CANONICAL_SITE_ROOT = "https://bof.lateinit.net"
private const val STRUCTURED_DATA_ID = "bof-structured-data"
private const val DEFAULT_OG_IMAGE_PATH = "/og-default-1200x630.png"

private val SEO_KEYWORDS = listOf(
    "개발자 밸런스게임",
    "개발자 질문",
    "개발자 토론",
    "버그 vs 기능",
    "프로그래머 밸런스",
    "개발자 커뮤니티",
    "개발 밈",
    "개발자 투표"
).joinToString(", ")

@Serializable
private data class ThingSchema(
    @SerialName("@type") val type: String = "Thing",
    val name: String
)

@Serializable
private data class SiteRefSchema(
    @SerialName("@type") val type: String = "WebSite",
    val name: String,
    val url: String
)

@Serializable
private data class StructuredDataSchema(
    @SerialName("@context") val context: String = "https://schema.org",
    @SerialName("@type") val type: String,
    val name: String,
    val description: String,
    val url: String,
    val inLanguage: String = SITE_LANGUAGE,
    val isPartOf: SiteRefSchema? = null,
    val about: List<ThingSchema> = emptyList()
)

fun applySeoMetadata(prompt: Prompt?) {
    val canonicalUrl = canonicalUrl(prompt?.id)
    val pageTitle = if (prompt == null) {
        "$SITE_NAME | 버그냐 기능이냐, 개발자 선택 게임"
    } else {
        "${trimText(prompt.a, 35)} vs ${trimText(prompt.b, 35)} | $SITE_NAME"
    }
    val pageDescription = if (prompt == null) {
        SITE_DESCRIPTION
    } else {
        "카테고리: ${prompt.category}. ${trimText(prompt.a, 60)} vs ${trimText(prompt.b, 60)} 중 하나를 선택해보세요."
    }
    val ogImage = "${CANONICAL_SITE_ROOT.trimEnd('/')}$DEFAULT_OG_IMAGE_PATH"

    document.title = pageTitle

    setMetaByName("description", pageDescription)
    setMetaByName("keywords", SEO_KEYWORDS)
    setMetaByName("robots", "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1")
    setMetaByName("author", "Bug or Feature")
    setMetaByName("theme-color", "#111827")

    setMetaByProperty("og:type", if (prompt == null) "website" else "article")
    setMetaByProperty("og:site_name", SITE_NAME)
    setMetaByProperty("og:locale", SITE_LOCALE)
    setMetaByProperty("og:title", pageTitle)
    setMetaByProperty("og:description", pageDescription)
    setMetaByProperty("og:url", canonicalUrl)
    setMetaByProperty("og:image", ogImage)

    setMetaByName("twitter:card", "summary")
    setMetaByName("twitter:title", pageTitle)
    setMetaByName("twitter:description", pageDescription)
    setMetaByName("twitter:image", ogImage)

    setCanonical(canonicalUrl)
    setStructuredData(prompt, canonicalUrl, pageTitle, pageDescription)
}

private fun canonicalUrl(promptId: String?): String {
    val base = CANONICAL_SITE_ROOT.trimEnd('/')
    return if (promptId.isNullOrBlank()) "$base/" else "$base/q/$promptId"
}

private fun trimText(text: String, maxLength: Int): String {
    if (text.length <= maxLength) return text
    return text.take(maxLength).trimEnd() + "..."
}

private fun setStructuredData(
    prompt: Prompt?,
    canonicalUrl: String,
    pageTitle: String,
    pageDescription: String
) {
    val origin = "${CANONICAL_SITE_ROOT.trimEnd('/')}/"
    val payload = if (prompt == null) {
        StructuredDataSchema(
            type = "WebSite",
            name = SITE_NAME,
            description = SITE_DESCRIPTION,
            url = origin
        )
    } else {
        StructuredDataSchema(
            type = "WebPage",
            name = pageTitle,
            description = pageDescription,
            url = canonicalUrl,
            isPartOf = SiteRefSchema(name = SITE_NAME, url = origin),
            about = listOf(
                ThingSchema(name = prompt.category),
                ThingSchema(name = trimText(prompt.a, 80)),
                ThingSchema(name = trimText(prompt.b, 80))
            )
        )
    }

    val script = (document.head?.querySelector("script#$STRUCTURED_DATA_ID") as? HTMLScriptElement)
        ?: document.createElement("script").unsafeCast<HTMLScriptElement>().also {
            it.id = STRUCTURED_DATA_ID
            it.type = "application/ld+json"
            document.head?.appendChild(it)
        }
    script.text = Json.encodeToString(payload)
}

private fun setCanonical(url: String) {
    val link = (document.head?.querySelector("link[rel='canonical']") as? HTMLLinkElement)
        ?: document.createElement("link").unsafeCast<HTMLLinkElement>().also {
            it.rel = "canonical"
            document.head?.appendChild(it)
        }
    link.href = url
}

private fun setMetaByName(name: String, content: String) {
    val meta = (document.head?.querySelector("meta[name='$name']") as? HTMLMetaElement)
        ?: document.createElement("meta").unsafeCast<HTMLMetaElement>().also {
            it.name = name
            document.head?.appendChild(it)
        }
    meta.content = content
}

private fun setMetaByProperty(property: String, content: String) {
    val meta = (document.head?.querySelector("meta[property='$property']") as? HTMLMetaElement)
        ?: document.createElement("meta").unsafeCast<HTMLMetaElement>().also {
            it.setAttribute("property", property)
            document.head?.appendChild(it)
        }
    meta.content = content
}
